<template>
  <div>
    <v-card class="mb-4">
      <search
        ref="search"
        :type="type"
        :loading="loading"
        :searching="searching"
        :show-save-monitor="!!entity.monitor"
        :show-create-monitor="type === searchTypes.REGULAR && $can('create', 'monitor')"
        :can-find-duplications="type === searchTypes.REGULAR"
        @searchBasic="searchBasic"
        @searchAdvanced="searchAdvanced"
        @save-monitor="saveMonitor"
        @create-monitor="createMonitor"
      />
    </v-card>

    <v-card id="totalResults">
      <search-result-tab
        ref="incidents"
        :type="type"
        :external-loading="loading"
        :header-indent="headerIndent"
        @add-flag="addFlag"
        @incident-deleted="reload"
        @commit-to-main-db="commitToMainDb"
        @overwrite-incident="overwriteIncident"
        @view-incident="viewIncident"
      />
    </v-card>
    <view-incident-popup
      ref="viewIncidentPopup"
      @add-flag="addFlag"
      @view-incident="viewIncident"
      @commit-to-main-db="commitToMainDb"
      @overwrite-incident="overwriteIncident"
    />
    <add-flag-popup
        ref="addFlagPopup"
        @sent="reload"
    />
    <edit-monitor-popup
      ref="editMonitorPopup"
      :has-delete-btn="false"
    />
    <confirmation ref="commitIncidentConfirmationPopup">
      <v-card-text
          v-if="type === searchTypes.UNFINISHED"
          class="pb-0"
      >
        <v-checkbox
            v-model="deleteOnDevice"
            hide-details
            label="Delete on device?"
        ></v-checkbox>
      </v-card-text>
    </confirmation>
  </div>
</template>

<script>
import _ from 'lodash';
import smoothscroll from 'smoothscroll-polyfill';
import { mapActions } from 'vuex';
import {
  LOAD_TABLE_FIELDS, LOAD_COLUMN_PRESETS, LOAD_SEARCH_FIELDS, LOAD_SEARCH_PRESETS,
} from '@/store/Incidents/actions';
import { SHOW_SNACKBAR } from '@/store/actions';
import {
  CLEAR_STATE, SET_SESSION_ID,
  SET_MONITOR, SET_SHOW_ADVANCED,
  SET_PAGINATION,
} from '@/store/Incidents/mutations';
import SearchResultTab from '@/components/Incidents/SearchResultTab';
import Search from '@/components/Incidents/Search';
import ViewIncidentPopup from '@/components/Incidents/ViewIncidentPopup';
import AddFlagPopup from '@/components/FlaggedUsers/AddFlagPopup';
import incidentFlagMixins from '@/mixins/incidentFlagMixins';
import SEARCH_TYPES from '@/enums/searchTypes';
import EditMonitorPopup from '@/components/Monitors/EditMonitorPopup';
import INCIDENT_FIELDS from '@/enums/incidentFields';
import incidents from '@/api/incidents';
import Confirmation from '@/components/Confirmation';

export default {
  ame: 'Incidents',
  mixins: [incidentFlagMixins],
  components: {
    Confirmation,
    EditMonitorPopup,
    AddFlagPopup,
    ViewIncidentPopup,
    Search,
    SearchResultTab,
  },

  props: {
    type: {
      type: String,
      required: true,
    },
    headerIndent: {
      type: Number,
      default: 450,
    },
  },

  data() {
    return {
      loading: false,
      searching: false,
      searchTypes: SEARCH_TYPES,
      target: 0,
      deleteOnDevice: false,
      options: {
        duration: 1000,
        offset: 0,
        easing: 'easeInOutCubic',
      },
    };
  },

  computed: {
    /**
     * Get incidents full state.
     *
     * @return {Object}
     */
    entity() {
      return this.$store.state.incidents[this.type];
    },
  },
  created() {
    const sessionId = window.history.state ? window.history.state.key : 1;
    const monitor = _.cloneDeep(this.entity.monitorTmp);
    if (!this.entity.sessionId || this.entity.sessionId !== sessionId) {
      this.clearState();
      this.setSessionId(sessionId);
    }
    if (monitor) {
      this.setMonitor(monitor);
    }
  },
  async mounted() {
    smoothscroll.polyfill();
    await this.fetchData();
    this.applyFilters();
  },

  methods: {
    ...mapActions({
      showSnackbar: SHOW_SNACKBAR,
    }),

    clearState() {
      this.$store.commit(`incidents/${this.type}/${CLEAR_STATE}`);
    },

    setSessionId(sessionId) {
      this.$store.commit(`incidents/${this.type}/${SET_SESSION_ID}`, sessionId);
    },

    setMonitor(monitor) {
      this.$store.commit(`incidents/${this.type}/${SET_MONITOR}`, monitor);
    },

    setShowAdvanced(value) {
      this.$store.commit(`incidents/${this.type}/${SET_SHOW_ADVANCED}`, value);
    },

    setPagination(pagination) {
      this.$store.commit(`incidents/${this.type}/${SET_PAGINATION}`, pagination);
    },

    loadTableFields() {
      return this.$store.dispatch(`incidents/${this.type}/${LOAD_TABLE_FIELDS}`);
    },

    loadColumnPresets() {
      return this.$store.dispatch(`incidents/${this.type}/${LOAD_COLUMN_PRESETS}`);
    },

    loadSearchFields() {
      return this.$store.dispatch(`incidents/${this.type}/${LOAD_SEARCH_FIELDS}`);
    },

    loadSearchPresets() {
      return this.$store.dispatch(`incidents/${this.type}/${LOAD_SEARCH_PRESETS}`);
    },

    /**
     * PreApply search filters by different conditions.
     */
    applyFilters() {
      if (this.entity.monitor) {
        this.setShowAdvanced(true);
        this.$refs.search.setConditions(this.entity.monitor.conditions);
      }
      if (this.$route.query.sequenceNumber) {
        this.$refs.search.setBasicFilters({
          sequenceNumbers: this.$route.query.sequenceNumber,
        }, true);
      }
      if (this.$route.query.filters && this.$route.query.filters === 'insights') {
        this.setInsightFilters();
      }
    },

    setInsightFilters() {
      const {
        category, focus, insight, dateFrom, dateTo,
      } = this.$route.query;
      const groups = [];
      if (dateFrom && dateTo) {
        this.$refs.search.setBasicFilters({
          dateRange: [
            dateFrom,
            dateTo,
          ],
        }, false);
      }
      groups.push({
        conditions: [
          {
            field: `insights_${category}`,
            focus,
            insight: insight || null,
            type: `insights_${category}`,
          },
        ],
        operator: 'and',
      });
      this.setShowAdvanced(true);
      this.$refs.search.setConditions(groups);
    },

    /**
     * Opens a popup for save existed monitor.
     *
     * @param {Array} filters - List of advanced search filters
     */
    saveMonitor(filters) {
      if (!this.entity.monitor) {
        return;
      }
      const { monitor } = this.entity;
      monitor.conditions = filters;
      this.$refs.editMonitorPopup.edit(monitor);
    },

    /**
     * Opens a popup for add new monitor.
     *
     * @param {Array} filters - List of advanced search filters
     */
    createMonitor(filters) {
      this.$refs.editMonitorPopup.create(filters);
    },

    /**
     * Open view incident dialog.
     *
     * @param {Object} incident - Incident object
     */
    viewIncident(incident) {
      const isNonRegular = this.type !== SEARCH_TYPES.REGULAR;
      let previousIncident = null;
      let nextIncident = null;
      this.entity.items.forEach((item, index) => {
        if (item.id === incident.id) {
          if (index > 0) {
            previousIncident = this.entity.items[index - 1];
          }
          if (index < this.entity.items.length - 1) {
            nextIncident = this.entity.items[index + 1];
          }
        }
      });
      const options = {
        showViewIncidentLink: !isNonRegular,
        showActions: !isNonRegular,
        showNonRegularActions: isNonRegular,
      };
      this.$refs.viewIncidentPopup.show(
        incident,
        options,
        null,
        previousIncident,
        nextIncident,
      );
    },

    /**
     * Fetch data for current view.
     */
    async fetchData() {
      this.loading = true;
      try {
        const promises = [];
        if (this.entity.tableFields === null) {
          promises.push(this.loadTableFields());
        }
        if (this.entity.columnPresets === null) {
          promises.push(this.loadColumnPresets());
        }
        if (this.entity.searchFields === null) {
          promises.push(this.loadSearchFields());
        }
        if (this.entity.searchPresets === null) {
          promises.push(this.loadSearchPresets());
        }
        await Promise.all(promises);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Is search results out of sight.
     *
     * @return {Boolean}
     */
    resultsOutOfSight() {
      const rect = this.$refs.incidents.$el.getBoundingClientRect();
      const resultsPos = rect.y;
      const indent = 110;
      const windowHeight = window.innerHeight || document.documentElement.clientHeight
          || document.body.clientHeight;
      return resultsPos + indent > windowHeight;
    },

    flushPaginationPage() {
      const pagination = _.cloneDeep(this.entity.pagination);
      pagination.page = 1;
      this.setPagination(pagination);
    },

    /**
     * Reload incidents initiated by clicking Search button.
     */
    async searchAdvanced() {
      try {
        this.searching = true;
        this.flushPaginationPage();
        await this.reload();
        if (this.resultsOutOfSight()) {
          if (this.entity.totalItems) {
            this.$refs.incidents.$el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else {
            this.showSnackbar({
              title: 'No search results matched by search params.',
              color: 'blue',
            });
          }
        }
      } finally {
        this.searching = false;
      }
    },

    /**
     * Reload incidents initiated automatically by changing basic filters.
     */
    async searchBasic() {
      this.flushPaginationPage();
      await this.reload();
      if (this.resultsOutOfSight()) {
        if (this.entity.totalItems) {
          this.showSnackbar(`${this.entity.totalItems} results have been found`);
        } else {
          this.showSnackbar({
            title: 'No search results matched by search params.',
            color: 'blue',
          });
        }
      }
    },

    /**
     * Reload incidents table data.
     */
    async reload() {
      await this.$refs.incidents.reloadItems();
    },

    /**
     * Commit incident to main db.
     *
     * @param {Object} incident
     */
    commitToMainDb(incident) {
      this.deleteOnDevice = false;
      this.$refs.commitIncidentConfirmationPopup.showConfirm(
        'Confirmation',
        `Do you really want to commit incident "${incident[INCIDENT_FIELDS.SEQUENCE_NUMBER]}"?`,
        async () => {
          await incidents.commitIncidentToMainDb(incident.id, this.deleteOnDevice);
          this.reload();
        },
      );
    },

    /**
     * Overwrite regular incident.
     *
     * @param {Object} incident
     */
    overwriteIncident(incident) {
      this.deleteOnDevice = false;
      this.$refs.commitIncidentConfirmationPopup.showConfirm(
        'Overwrite existing?',
        'If you choose to continue with the overwrite, all previous data from the existing '
          + 'report will be lost and can not be undone. Do you wish to continue?',
        async () => {
          await incidents.commitIncidentToMainDb(incident.id, this.deleteOnDevice);
          this.reload();
        },
      );
    },
  },
};
</script>
